import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { useReducedMotion } from "framer-motion";
import { ChevronsLeft, ChevronsRight, ArrowRight, Pause, Play } from "lucide-react";
import { CAROUSEL_SERVICES, HOME_SERVICE_CARD_CLASS, HOME_SERVICE_TITLE_CLASS } from "@/lib/site";
import { trackSpotlight } from "@/lib/spotlight";

export const ServicesCarousel = () => {
  const reducedMotion = useReducedMotion();
  const [viewport, api] = useEmblaCarousel({ loop: true, align: "start", duration: 30, inViewThreshold: 0.6 });
  const root = useRef(null);
  const [selected, setSelected] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState([0]);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(root.current);
    const visibility = () => setPageVisible(!document.hidden);
    visibility();
    document.addEventListener("visibilitychange", visibility);
    return () => { observer.disconnect(); document.removeEventListener("visibilitychange", visibility); };
  }, []);

  useEffect(() => {
    if (!api) return undefined;
    const sync = () => { setSelected(api.selectedScrollSnap()); setVisibleSlides(api.slidesInView()); };
    const down = () => setDragging(true);
    const up = () => setDragging(false);
    sync();
    api.on("select", sync).on("slidesInView", sync).on("reInit", sync).on("pointerDown", down).on("pointerUp", up);
    return () => { api.off("select", sync).off("slidesInView", sync).off("reInit", sync).off("pointerDown", down).off("pointerUp", up); };
  }, [api]);

  useEffect(() => {
    if (!api || reducedMotion || paused || hovered || focused || dragging || !inView || !pageVisible) return undefined;
    const timer = window.setTimeout(() => api.scrollNext(), 4000);
    return () => window.clearTimeout(timer);
  }, [api, selected, reducedMotion, paused, hovered, focused, dragging, inView, pageVisible]);

  return (
    <div ref={root} className="services-carousel" role="region" aria-label="Core Banking services" aria-roledescription="carousel"
      data-testid="services-carousel" data-selected={selected}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <div className="services-carousel__frame">
      <div ref={viewport} className="services-carousel__viewport" id="home-service-slides">
        <div className="services-carousel__track">
          {CAROUSEL_SERVICES.map((service, index) => (
            <div className="services-carousel__slide" key={service.slug} role="group" aria-roledescription="slide"
              aria-label={`${index + 1} of ${CAROUSEL_SERVICES.length}`} aria-hidden={!visibleSlides.includes(index)}>
              <Link to={service.homePath || "/services"} data-testid={`home-service-${service.slug}`}
                tabIndex={visibleSlides.includes(index) ? 0 : -1} className={`${HOME_SERVICE_CARD_CLASS} service-spotlight raised-surface`}
                onPointerMove={trackSpotlight}>
                <div className="gold-icon-tile w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
                  <service.icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div className={HOME_SERVICE_TITLE_CLASS}>
                  <h3 className="text-xl font-semibold text-slate-900 font-heading">{service.title}</h3>
                </div>
                <p className="mt-3 text-slate-600 leading-relaxed">{service.homeShort || service.short}</p>
                <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                  Learn more <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          ))}
        </div>
      </div>
        <button type="button" className="services-carousel__button services-carousel__button--next" aria-controls="home-service-slides" aria-label="Next service" title="Next service"
          onClick={() => api?.scrollNext(Boolean(reducedMotion))} data-testid="services-next">
          <ChevronsRight size={22} aria-hidden="true" />
        </button>
        <button type="button" className="services-carousel__button services-carousel__button--previous" aria-controls="home-service-slides" aria-label="Previous service" title="Previous service"
          onClick={() => api?.scrollPrev(Boolean(reducedMotion))} data-testid="services-previous">
          <ChevronsLeft size={22} aria-hidden="true" />
        </button>
      </div>
      <div className="services-carousel__controls">
        <div className="services-carousel__status">
          <span aria-hidden="true">{selected + 1} / {CAROUSEL_SERVICES.length}</span>
          {!reducedMotion && <button type="button" className="services-carousel__pause" onClick={() => setPaused(!paused)}
            aria-label={paused ? "Resume automatic service rotation" : "Pause automatic service rotation"}
            title={paused ? "Resume automatic service rotation" : "Pause automatic service rotation"}>
            {paused ? <Play size={16} /> : <Pause size={16} />}
          </button>}
        </div>
      </div>
    </div>
  );
};
